import React from "react";
import { Link } from "react-router-dom";
import { Check, Circle } from "lucide-react";

// Map step keys → the espace-client route to CTA the user towards
const STEP_LINKS = {
  compte: "/espace-client/profil",
  devis: "/espace-client/documents",
  devis_signe: "/espace-client/documents",
  acompte: "/espace-client/documents",
  questionnaire: null, // decided at render-time (mariage vs prep)
  playlist: "/espace-client/playlist",
  evenement: "/espace-client/rdv",
};

/**
 * Elegant client progression bar for the Espace Client dashboard.
 * Renders a horizontal stepper with completed / pending indicators.
 *
 * Props:
 *   - progression: { steps: [{ key, label, done }], percent, done_count, total }
 *   - isMariage: bool — used to route the "Questionnaire" step correctly
 */
export default function ClientProgress({ progression, isMariage }) {
  if (!progression || !progression.steps) return null;

  const stepLink = (s) => {
    if (s.key === "questionnaire") return isMariage ? "/espace-client/questionnaire" : "/espace-client/questionnaire-prep";
    return STEP_LINKS[s.key] || null;
  };

  const currentIdx = progression.steps.findIndex((s) => !s.done);
  const nextStep = currentIdx >= 0 ? progression.steps[currentIdx] : null;

  return (
    <section className="card-luxe p-6 md:p-8 mb-10" data-testid="ec-progress" id="progression-bar">
      <div data-testid="progression-bar" style={{ display: "contents" }}>
      <div className="flex items-baseline justify-between flex-wrap gap-3 mb-6">
        <div>
          <p className="eyebrow">Avancement de votre dossier</p>
          <h2 className="font-serif-luxe text-xl md:text-2xl mt-2">
            <span className="gold-text">{progression.done_count}</span>
            <span className="text-white/40 text-lg mx-1">/</span>
            <span>{progression.total}</span>
            <span className="text-white/55 text-sm md:text-base ml-3">étapes complétées</span>
          </h2>
        </div>
        {nextStep ? (
          <Link
            to={stepLink(nextStep) || "#"}
            data-testid="ec-progress-next-cta"
            className="text-[11px] uppercase tracking-[0.22em] text-[#DCB457] border border-[#DCB457]/45 px-3.5 py-2 hover:bg-[#DCB457]/10 transition"
          >
            Prochaine étape : {nextStep.label} →
          </Link>
        ) : (
          <span className="text-[11px] uppercase tracking-[0.22em] text-emerald-300/85 border border-emerald-400/40 px-3.5 py-2">
            ✓ Dossier complet
          </span>
        )}
      </div>

      {/* Progress bar */}
      <div className="relative h-1.5 bg-white/8 mb-8 overflow-hidden" data-testid="ec-progress-bar">
        <div
          className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#DCB457] to-[#f0d888] transition-all duration-700"
          style={{ width: `${progression.percent}%` }}
          data-testid="ec-progress-bar-fill"
        />
      </div>

      {/* Steps grid */}
      <ol className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="ec-progress-steps">
        {progression.steps.map((s, i) => {
          const isCurrent = i === currentIdx;
          const to = stepLink(s);
          const inner = (
            <div
              className={`h-full flex flex-col items-center text-center gap-2 p-3 border transition ${
                s.done
                  ? "border-[#DCB457]/40 bg-[#DCB457]/[0.04]"
                  : isCurrent
                    ? "border-[#DCB457] bg-[#DCB457]/[0.08]"
                    : "border-white/8 opacity-55"
              }`}
              data-testid={`ec-progress-step-${s.key}`}
            >
              <span
                className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${
                  s.done
                    ? "bg-[#DCB457] text-black"
                    : isCurrent
                      ? "border border-[#DCB457] text-[#DCB457]"
                      : "border border-white/20 text-white/45"
                }`}
              >
                {s.done ? <Check size={13} strokeWidth={2.2} /> : <Circle size={11} strokeWidth={1.5} />}
              </span>
              <p
                className={`text-[11px] leading-tight ${
                  s.done ? "text-white/85" : isCurrent ? "text-[#DCB457]" : "text-white/45"
                }`}
              >
                {s.label}
              </p>
              <span className="text-[9px] uppercase tracking-[0.18em] text-white/35">Étape {i + 1}</span>
            </div>
          );
          return (
            <li key={s.key} className="min-h-[92px]">
              {to && !s.done ? (
                <Link to={to} className="block h-full hover:opacity-100">{inner}</Link>
              ) : (
                inner
              )}
            </li>
          );
        })}
      </ol>
      </div>
    </section>
  );
}
